import {makeAutoObservable} from "mobx";
import CsInstance from "./CsInstance";
export type SettingsModalModes = "settings" | "fontSubsettings";

/**
 * Default font size for the editor and console, in rem.
 * For the standard 1rem = 16px, this is 14px.
 */
export const DEFAULT_EDITOR_FONT_SIZE = 0.875;
const MIN_EDITOR_FONT_SIZE = 0.375; // 6px
const FONT_SIZE_STEP = 0.125;

/**
 * This store is used to manage the UI state. Things that should go here should
 * be things that don't affect how the Theater or Pixi play -- for example,
 * whether a modal is open, or whether to show coordinates.
 */
export class UiStore {
	private _theaterSettingsOpen = false;
	private _theaterSettingsModalMode: SettingsModalModes = "settings";
	private _shouldCloseTheaterSettingsModal = true;
	private _showCoordinates = true;
	private _isSidebarShown = true;

	// Context menu, which is currently only used by the vanilla CodeMirror editor
	private _showContextMenu = false;
	private _contextMenuPosition = {x: 0, y: 0};

	private _editorFontSize = DEFAULT_EDITOR_FONT_SIZE;

	private _csInstance: CsInstance;

	constructor(props: {csInstance: CsInstance}) {
		this._csInstance = props.csInstance;
		makeAutoObservable(this);
	}

	increaseFontSize() {
		const newFontSize = this._editorFontSize + FONT_SIZE_STEP;
		this.directlySetFontSize(newFontSize);
	}

	decreaseFontSize() {
		const newFontSize = Math.max(
			this._editorFontSize - FONT_SIZE_STEP,
			MIN_EDITOR_FONT_SIZE
		);
		this.directlySetFontSize(newFontSize);
	}

	resetFontSize() {
		this.directlySetFontSize(DEFAULT_EDITOR_FONT_SIZE);
	}

	directlySetFontSize(size: number) {
		this._editorFontSize = size;
		this._csInstance.codeStore.codeMirrorStore.setEditorFontSize(
			this._editorFontSize
		);
	}

	/**
	 * Handle keyboard shortcuts for the editor.
	 * `Meta` + `=` to increase font size
	 * `Meta` + `-` to decrease font size
	 * `Meta` + `0` to reset font size
	 * `Meta` + `S` or `Meta` + `Enter` to compile
	 * @param e The keyboard event.
	 */
	onKeyDownHandler = (e: KeyboardEvent, isEditorFocused: boolean) => {
		if (!e.metaKey || !isEditorFocused) {
			return;
		}
		if (e.key === "=") {
			e.preventDefault();
			this.increaseFontSize();
		} else if (e.key === "-") {
			e.preventDefault();
			this.decreaseFontSize();
		} else if (e.key === "0") {
			e.preventDefault();
			this.resetFontSize();
		} else if (e.key.toUpperCase() === "S" || e.key.toUpperCase() === "ENTER") {
			// meta+S or meta+shift+enter to compile
			e.preventDefault();
			this._csInstance.compileStore.attemptToCompileCode();
		}
	};

	// autogen getters and setters for MobX actions

	get theaterSettingsOpen() {
		return this._theaterSettingsOpen;
	}

	set theaterSettingsOpen(value) {
		// When opening/closing the modal, default to basic settings mode.
		if (value !== this._theaterSettingsOpen) {
			this.theaterSettingsModalMode = "settings";
		}
		this._theaterSettingsOpen = value;
	}

	get theaterSettingsModalMode() {
		return this._theaterSettingsModalMode;
	}

	set theaterSettingsModalMode(value) {
		this._theaterSettingsModalMode = value;
	}

	get shouldCloseTheaterSettingsModal() {
		return this._shouldCloseTheaterSettingsModal;
	}

	set shouldCloseTheaterSettingsModal(value) {
		this._shouldCloseTheaterSettingsModal = value;
	}

	get showCoordinates() {
		return this._showCoordinates;
	}

	set showCoordinates(value) {
		this._showCoordinates = value;
	}

	get isSidebarShown() {
		return this._isSidebarShown;
	}

	set isSidebarShown(value) {
		this._isSidebarShown = value;
	}

	get showContextMenu() {
		return this._showContextMenu;
	}

	set showContextMenu(value) {
		this._showContextMenu = value;
	}

	get contextMenuPosition() {
		return this._contextMenuPosition;
	}

	set contextMenuPosition(value) {
		this._contextMenuPosition = value;
	}

	get editorFontSize() {
		return this._editorFontSize;
	}
}

export default UiStore;
